import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import { cacheScope, clearCacheOnAuthFailure, readCache, writeCache, invalidateCache } from "@/lib/ui-cache";
import { sessionPreview } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";
import i18n from "@/lib/i18n";
import { addSessionToList, removeSessionFromList } from "@/api/session-participation";
import {
  DEFAULT_SESSION_TITLE,
  isSchedulerSession,
} from "@/lib/session";
import type {
  Session,
  SessionCreateRequest,
  SessionUpdateRequest,
  SessionListResponse,
} from "@/lib/session";

const API_BASE = "/api/sessions";
const TENANT_DEFAULT_URL = "/api/sessions/tenant-default";
const SESSION_DIAL_URL = (sessionId: string) => `${API_BASE}/${sessionId}/dial`;

export const SESSIONS_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const jsonHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

/** Mirrors the bridge's safe ``server-slug/agent-id`` recognition. */
function isRemoteAgentRef(value: string | null | undefined): value is string {
  if (!value) return false;
  const slash = value.indexOf("/");
  if (slash <= 0) return false;
  const slug = value.slice(0, slash);
  const agentId = value.slice(slash + 1);
  return /^[a-z0-9][a-z0-9-]*$/.test(slug) && !!agentId && !agentId.includes("/");
}

async function dialRemoteSession(
  sessionId: string,
  agentId: string,
  getToken: () => Promise<string>,
): Promise<boolean> {
  if (!isRemoteAgentRef(agentId)) return false;
  const res = await fetchWithAuth(
    SESSION_DIAL_URL(sessionId),
    {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ agent_id: agentId }),
    },
    getToken,
  );
  if (!res.ok) {
    let detail = `HTTP ${res.status}: ${res.statusText}`;
    try {
      const body = await res.json() as { error?: unknown };
      if (typeof body.error === "string" && body.error) detail = body.error;
    } catch {
      // The status fallback above is actionable enough for a malformed proxy response.
    }
    throw new Error(detail);
  }
  const result = await res.json() as { dialed?: unknown };
  return result.dialed === true;
}

export interface TenantDefault {
  entityId: string;
  entityType: "agent" | "team";
}

interface ReplacementRequest {
  settled: Promise<void>;
  resolve: () => void;
}

/**
 * A reset is authoritative for the current query. Background refreshes are
 * intentionally non-destructive while the user is paging through history.
 */
export type SessionRefreshMode = "reset" | "background";

/**
 * Reconcile a refreshed page with already-loaded rows without duplicate IDs.
 * Earlier collections win so server-refreshed rows replace stale local copies.
 */
function mergeSessionsById(...collections: Array<readonly Session[]>): Session[] {
  const seen = new Set<string>();
  const merged: Session[] = [];
  for (const collection of collections) {
    for (const session of collection) {
      if (seen.has(session.id)) continue;
      seen.add(session.id);
      merged.push(session);
    }
  }
  return merged;
}

export interface UseSessionsReturn {
  sessions: Session[];
  /** True after at least one authoritative session-list response succeeded. */
  loaded: boolean;
  cacheState: "none" | "cached-revalidating" | "stale" | "live";
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  /** True when a load-more request needs an explicit retry after a replacement failed. */
  canRetryLoadMore: boolean;
  error: string | null;
  /** Server-side search query (owned by the hook so all data fetching
   *  lives in one place; the sidebar is purely presentational). */
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  /** The debounced query that the current ``sessions`` array was actually
   *  fetched with.  When non-empty, ``sessions`` is a server-filtered
   *  subset; when empty, it is the full (unfiltered) list.  This is always
   *  in sync with ``sessions`` — it is the query the current array was
   *  fetched with — unlike ``searchQuery`` which updates instantly on every
   *  keystroke.  Used by the auto-select / auto-create effects in ChatPage
   *  to skip those branches while a search is active (they operate on the
   *  ``sessions`` array directly). */
  debouncedQuery: string;
  /** Unfiltered set of session IDs known to the UI, including IDs from
   *  flat listings, child-session loads, and direct validation. Search
   *  filtering never removes IDs from this set. */
  knownSessionIds: Set<string>;
  /** Adds session IDs discovered outside the flat session list, such as
   *  lazily loaded child sessions. */
  registerKnownSessionIds: (ids: string[]) => void;
  /** True while a search request is in flight (shows a spinner in the
   *  search field). */
  searching: boolean;
  create: (
    title?: string,
    agentId?: string | null,
    metadata?: SessionCreateRequest["metadata"],
    projectId?: string | null,
  ) => Promise<Session | null>;
  rename: (id: string, title: string) => Promise<Session | null>;
  updateAgent: (id: string, agentId: string | null) => Promise<Session | null>;
  archive: (id: string) => Promise<boolean>;
  removeParticipation: (id: string) => Promise<void>;
  addParticipation: (id: string) => Promise<void>;
  /** Defaults to an intentional, authoritative query reset. */
  refresh: (mode?: SessionRefreshMode) => Promise<void>;
  loadMore: () => Promise<void>;
  /** Idempotently retries a deferred/failed load-more request. */
  retryLoadMore: () => Promise<void>;
  getTenantDefault: () => Promise<TenantDefault | null>;
}

/**
 * Hook to manage sessions backed by the Agno OS API.
 *
 * Supports cursor-based pagination (``limit`` + ``before`` cursor) and
 * server-side search (``q`` param) with a debounced query.
 *
 * Accepts a getToken function from AuthKit so that a fresh WorkOS access
 * token is fetched before every request, avoiding stale-token 401s.
 */
export function useSessions(getToken: () => Promise<string>): UseSessionsReturn {
  const verifiedScope = useCacheScope();
  const previousScopeRef = useRef(verifiedScope);
  const [cacheState, setCacheState] = useState<UseSessionsReturn["cacheState"]>("none");
  const [sessions, setSessions] = useState<Session[]>([]);
  // React state is asynchronous, but refresh reconciliation needs the newest
  // list at the instant a network response lands (including optimistic edits).
  // Keep that authoritative local snapshot in lockstep with every write.
  const sessionsRef = useRef<Session[]>([]);
  const listQueryRef = useRef("");
  const commitSessions = useCallback(
    (update: (current: Session[]) => Session[]): Session[] => {
      const next = update(sessionsRef.current);
      sessionsRef.current = next;
      setSessions(next);
      return next;
    },
    [],
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [canRetryLoadMore, setCanRetryLoadMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `loading=false` after an error must not look like a confirmed empty list
  // to ChatPage's auto-create logic.
  const [loaded, setLoaded] = useState(false);

  // ── Known session IDs (unfiltered) ────────────────────────────────────────
  // A Set of session IDs discovered from the flat list, child-session
  // loads, direct validation, and locally created sessions. Unlike
  // ``sessions`` (which becomes a filtered subset during search), search
  // filtering never removes IDs from this Set.
  //
  // ChatPage's session-validation effect reads this instead of scanning
  // ``sessions`` so that "active session not in the sidebar list" is no
  // longer ambiguous between "needs validation" and "filtered out by
  // search".
  // This decouples the guard from search state entirely: it doesn't need to
  // know whether a search is active, what the query is, or whether the
  // debounce has fired.
  //
  // Maintenance rules:
  //   • Rebuilt from flat-list IDs on every **intentional unfiltered**
  //     first-page reset. A background refresh with loaded history rebuilds
  //     from its reconciled visible rows so it cannot forget that tail. Child
  //     IDs are re-added when their parent is expanded or the session is
  //     validated.
  //   • Additive (unioned) on search first-page fetches and load-more — so
  //     the active session stays "known" even when a search filters it out
  //     of ``sessions``.
  //   • Locally created and lazily loaded child sessions are added
  //     immediately.
  //   • Archived sessions are removed immediately in ``archive()``.
  const [knownSessionIds, setKnownSessionIds] = useState<Set<string>>(new Set());

  const registerKnownSessionIds = useCallback((ids: string[]) => {
    setKnownSessionIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  // ── Pending created sessions ──────────────────────────────────────────────
  // Sessions created locally via create() that may not yet appear in a
  // server-fetched page.  fetchPage preserves these when replacing the list
  // so a refresh triggered by a run-status change doesn't blow away a
  // just-prepended session.  Each entry is removed once the server response
  // includes it (matched by id).
  const pendingCreatedRef = useRef<Session[]>([]);

  // ── Server-side search state ──────────────────────────────────────────────
  // Owned by the hook so all data fetching lives in one place. The sidebar
  // is purely presentational — it calls setSearchQuery and the hook handles
  // the debounced re-fetch.
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searching, setSearching] = useState(false);

  // Refs to read current values in async callbacks without re-subscribing.
  const hasMoreRef = useRef(false);
  const nextCursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);
  // True once at least one older page has been appended to the current query.
  // Unlike nextCursor, this distinguishes a one-page list with more available
  // from a user actively browsing already-loaded history.
  const hasLoadedMoreRef = useRef(false);
  const debouncedQueryRef = useRef("");
  // Session-list requests can originate from the initial load, notification
  // refreshes, search, and pagination. Their responses are not ordered on the
  // wire, so only the most recently started request may update list state.
  // Without this guard, an older refresh can land after a newer response has
  // confirmed a newly created session, erase its optimistic row, and rebuild
  // knownSessionIds without it.
  const latestListRequestIdRef = useRef(0);
  const replaceRequestInFlightRef = useRef(false);
  // A load-more request can arrive from Virtuoso while a refresh or search is
  // replacing page one. Keep a completion signal for the *current*
  // replacement so that request can wait for the new cursor instead of being
  // dropped. Starting a newer replacement resolves the previous signal, which
  // lets waiters follow the newer request rather than waiting on stale I/O.
  const activeReplaceRequestRef = useRef<ReplacementRequest | null>(null);
  // A failed replacement leaves the prior list/cursor in place; do not append
  // against that stale cursor with the new query in that case.
  const latestReplacementOutcomeRef = useRef<boolean | null>(null);
  const sessionsLoadedRef = useRef(false);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { nextCursorRef.current = nextCursor; }, [nextCursor]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { debouncedQueryRef.current = debouncedQuery; }, [debouncedQuery]);

  // ── Debounce search input ─────────────────────────────────────────────────
  // Wait SEARCH_DEBOUNCE_MS after the user stops typing before firing a
  // request. This avoids hammering the server on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const waitForReplacement = useCallback(async (): Promise<boolean> => {
    let waitedForReplacement = false;
    while (replaceRequestInFlightRef.current) {
      waitedForReplacement = true;
      const activeRequest = activeReplaceRequestRef.current;
      if (!activeRequest) return false;
      await activeRequest.settled;
    }
    return !waitedForReplacement || latestReplacementOutcomeRef.current === true;
  }, []);

  // ── Unified fetch function ────────────────────────────────────────────────
  // Fetches a single page from the API. When ``append`` is true, the new
  // sessions are appended to the existing array (loadMore); otherwise an
  // intentional reset replaces the list while a background refresh reconciles
  // its first page with already-loaded history.
  const fetchPage = useCallback(
    async (
      cursor: string | null,
      query: string,
      append: boolean,
      refreshMode: SessionRefreshMode = "reset",
    ): Promise<boolean> => {
      // A replacement invalidates any in-flight pagination request. Conversely,
      // do not start pagination while a replacement is in flight: its cursor
      // belongs to the list that is about to be replaced.
      if (append && replaceRequestInFlightRef.current) return false;

      // A background refresh can safely update page one without resetting an
      // active history browse. Snapshot the tail metadata before the request:
      // an in-flight append is invalidated by the request-ID guard below, and
      // the preserved keyset cursor remains valid when newer rows are added.
      const preserveLoadedTail = (
        !append
        && refreshMode === "background"
        && hasLoadedMoreRef.current
        // A failed search leaves the old query's rows visible. Do not merge
        // those rows into a background result for a different query.
        && query === listQueryRef.current
      );
      const preservedHasMore = hasMoreRef.current;
      const preservedNextCursor = nextCursorRef.current;

      const requestId = latestListRequestIdRef.current + 1;
      const requestScope = cacheScope();
      latestListRequestIdRef.current = requestId;
      let replaceRequest: ReplacementRequest | null = null;
      let replacementApplied = false;
      if (!append) {
        // Wake waiters on an older replacement so they can follow this newer
        // request instead of remaining tied to a stale network response.
        latestReplacementOutcomeRef.current = null;
        activeReplaceRequestRef.current?.resolve();
        let resolveReplace!: () => void;
        const settled = new Promise<void>((resolve) => {
          resolveReplace = resolve;
        });
        replaceRequest = { settled, resolve: resolveReplace };
        activeReplaceRequestRef.current = replaceRequest;
        replaceRequestInFlightRef.current = true;
      }

      try {
        const params = new URLSearchParams();
        params.set("limit", String(SESSIONS_PAGE_SIZE));
        params.set("include_threads", "true");
        if (cursor) params.set("before", cursor);
        if (query.trim()) params.set("q", query.trim());

        const url = `${API_BASE}?${params}`;
        const listFetchStartedAt = Date.now();
        const res = await fetchWithAuth(url, { headers: {} }, getToken);
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            setError(`HTTP ${res.status}: ${res.statusText}`);
            clearCacheOnAuthFailure();
            commitSessions(() => []);
            sessionsLoadedRef.current = false;
            setLoaded(false);
            setKnownSessionIds(new Set());
            setCacheState("none");
            void invalidateCache("sessionPages");
          }
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        const data: SessionListResponse = await res.json();
        // The server excludes these rows on current deployments. Keep this
        // metadata-based fallback for independently deployed older servers,
        // which have always returned ``metadata`` but may still list them.
        const listedSessions = Array.isArray(data.sessions)
          ? data.sessions.filter((session) => !isSchedulerSession(session)).map((session) => ({
              ...session,
              list_fetch_started_at: listFetchStartedAt,
            }))
          : [];

        // A newer search, refresh, or page request has already become the
        // source of truth. Do not let this stale response mutate either the
        // rendered rows or the stale-session guard's ID index.
        if (requestId !== latestListRequestIdRef.current || requestScope !== cacheScope()) {
          return false;
        }
        if (!append && !query.trim() && !cursor) {
          const snapshot = listedSessions.map(sessionPreview).filter((row): row is Session => !!row);
          void writeCache("sessionPages", "first", "sidebar", { sessions: snapshot, has_more: data.has_more === true, next_cursor: data.next_cursor ?? null });
        }

        // Snapshot and update pending creations before scheduling React state
        // work. Mutating this ref inside a state updater meant a newer response
        // could clear it and then an older response could use the changed ref.
        const serverIds = new Set(listedSessions.map((session) => session.id));
        const pendingSessions = append
          ? []
          : pendingCreatedRef.current.filter((session) => !serverIds.has(session.id));
        if (!append) pendingCreatedRef.current = pendingSessions;

        const nextSessions = append
          ? commitSessions((prev) => [...prev, ...listedSessions])
          : preserveLoadedTail
            // Keep the historical tail visible. A row missing from a newly
            // fetched first page may simply have shifted past that page's
            // boundary after a newer session arrived, so dropping it would
            // silently erase loaded history. ID-based reconciliation gives
            // page-one rows server precedence while retaining each older row
            // exactly once. Intentional resets remain fully authoritative.
            ? commitSessions((prev) => mergeSessionsById(
                pendingSessions,
                listedSessions,
                prev,
              ))
            : commitSessions(() => mergeSessionsById(pendingSessions, listedSessions));
        if (append) {
          hasLoadedMoreRef.current = true;
        } else if (!preserveLoadedTail) {
          hasLoadedMoreRef.current = false;
        }
        if (!append) listQueryRef.current = query;

        // ── Maintain knownSessionIds ───────────────────────────────────────
        // On an unfiltered first-page fetch (refresh with no search), rebuild
        // the Set so IDs for archived/deleted-from-elsewhere sessions are
        // dropped. On search and load-more, union IDs so the active session
        // stays known even when a search filters it out of the sidebar.
        const fetchedIds = listedSessions.map((session) => session.id);
        const isUnfilteredReplace = !append && !query.trim();
        if (isUnfilteredReplace) {
          // A background merge has deliberately retained a loaded tail, so
          // rebuilding its index from page one alone would make those visible
          // rows look stale to the session-validation guard.
          setKnownSessionIds(new Set(
            preserveLoadedTail
              ? nextSessions.map((session) => session.id)
              : [
                  ...fetchedIds,
                  ...pendingSessions.map((session) => session.id),
                ],
          ));
        } else {
          setKnownSessionIds((prev) => {
            const next = new Set(prev);
            for (const id of fetchedIds) next.add(id);
            return next;
          });
        }

        const nextHasMore = preserveLoadedTail
          ? preservedHasMore
          : (data.has_more ?? false);
        const nextCursor = preserveLoadedTail
          ? preservedNextCursor
          : (data.next_cursor ?? null);
        // React effects update these refs after commit, but Virtuoso can call
        // endReached before then; it must never use the previous page cursor.
        hasMoreRef.current = nextHasMore;
        nextCursorRef.current = nextCursor;
        setHasMore(nextHasMore);
        setNextCursor(nextCursor);
        setCanRetryLoadMore(false);
        if (!append) replacementApplied = true;
        setCacheState("live");
        return true;
      } catch (err) {
        // A failed request that has been superseded must not surface an error
        // for data the UI no longer intends to display.
        if (requestId !== latestListRequestIdRef.current || requestScope !== cacheScope()) {
          return false;
        }
        throw err;
      } finally {
        if (!append && replaceRequest) {
          if (requestId === latestListRequestIdRef.current) {
            latestReplacementOutcomeRef.current = replacementApplied;
            replaceRequestInFlightRef.current = false;
            if (activeReplaceRequestRef.current === replaceRequest) {
              activeReplaceRequestRef.current = null;
            }
          }
          replaceRequest.resolve();
        }
      }
    },
    [commitSessions, getToken],
  );

  // ── refresh() — first page (no search, or current search) ─────────────────
  // Only set loading=true on the very first load (when sessions is still
  // empty).  On subsequent refreshes — e.g. handleStatusChange firing after
  // a run completes — keep loading=false so the sidebar list stays mounted.
  // The sidebar renders the Virtuoso list only when `!loading`, so flipping
  // loading to true unmounts the entire list, causing a visual flicker where
  // the active-session highlight disappears and the user clicks the wrong
  // session row when the list remounts.
  const refresh = useCallback(async (mode: SessionRefreshMode = "reset") => {
    const isFirstLoad = !sessionsLoadedRef.current;
    if (isFirstLoad) setLoading(true);
    setError(null);
    try {
      const applied = await fetchPage(
        null,
        debouncedQueryRef.current,
        false,
        mode,
      );
      if (applied) {
        sessionsLoadedRef.current = true;
        setLoaded(true);
      }
    } catch (err: any) {
      setError(err?.message ?? i18n.t("sidebar.errors.loadSessions"));
      setCacheState((current) => current === "cached-revalidating" ? "stale" : current);
    } finally {
      // If a newer replacement is still in flight, let that request finish the
      // initial-load state instead of briefly showing an empty sidebar.
      if (isFirstLoad && !replaceRequestInFlightRef.current) setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    if (previousScopeRef.current === verifiedScope) return;
    previousScopeRef.current = verifiedScope;
    latestListRequestIdRef.current += 1;
    sessionsLoadedRef.current = false;
    commitSessions(() => []);
    setLoaded(false);
    setKnownSessionIds(new Set());
    setCacheState("none");
    if (!verifiedScope) return;
    let cancelled = false;
    void readCache<SessionListResponse>("sessionPages", "first", "sidebar").then((snapshot) => {
      if (cancelled || cacheScope() !== verifiedScope || sessionsLoadedRef.current || debouncedQueryRef.current || !snapshot?.sessions?.length) return;
      const safe = snapshot.sessions.map(sessionPreview).filter((row): row is Session => !!row);
      if (!safe.length) return;
      commitSessions(() => safe);
      setCacheState("cached-revalidating");
      setLoading(false);
    });
    void refresh();
    return () => { cancelled = true; latestListRequestIdRef.current += 1; };
  }, [verifiedScope]);

  const removeParticipation = useCallback(async (id: string) => {
    try {
      await removeSessionFromList(id, getToken);
      void invalidateCache("sessionPages");
      void invalidateCache("contextDetails", "session", id);
      void invalidateCache("messageTails", "tail", id);
      commitSessions((current) => current.filter((session) => session.id !== id));
      setKnownSessionIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    } catch (error) {
      await refresh("reset");
      throw error;
    }
    await refresh("reset");
  }, [commitSessions, getToken, refresh]);

  const addParticipation = useCallback(async (id: string) => {
    try {
      await addSessionToList(id, getToken);
      void invalidateCache("sessionPages");
      commitSessions((current) => current.map((session) => session.id === id
        ? { ...session, is_pinned: true, pin_source: "manual" as const }
        : session));
      setKnownSessionIds((current) => new Set(current).add(id));
    } catch (error) {
      await refresh("reset");
      throw error;
    }
    await refresh("reset");
  }, [commitSessions, getToken, refresh]);

  useRefreshOnReconnect(() => refresh("background"));

  // ── Search effect — re-fetch first page when debouncedQuery changes ───────
  // Skip the initial mount (refresh() handles that). Only fire when the
  // query actually changes from a user action.
  const isFirstRun = useRef(true);
  const searchRequestIdRef = useRef(0);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const searchRequestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = searchRequestId;
    const isFirstLoad = !sessionsLoadedRef.current;
    setSearching(true);
    setError(null);
    void fetchPage(null, debouncedQuery, false)
      .then((applied) => {
        if (applied) {
          sessionsLoadedRef.current = true;
          setLoaded(true);
        }
      })
      .catch((err: any) => {
        if (searchRequestId === searchRequestIdRef.current) {
          setError(err?.message ?? i18n.t("sidebar.errors.searchFailed"));
        }
      })
      .finally(() => {
        // A later query owns the spinner and initial-load state now.
        if (searchRequestId !== searchRequestIdRef.current) return;
        setSearching(false);
        if (isFirstLoad && !replaceRequestInFlightRef.current) setLoading(false);
      });
    // intentionally NOT depending on fetchPage — fetchPage is stable via
    // getToken, and we don't want a fetchPage identity change to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // ── loadMore() — next page (append older sessions) ────────────────────────
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;

    while (true) {
      // endReached is effectively one-shot for a given final item. If it
      // fires during a replacement, wait for that replacement's fresh cursor
      // rather than returning and relying on Virtuoso to emit it again.
      if (!(await waitForReplacement())) {
        // The prior list/cursor is still valid after a failed replacement,
        // but Virtuoso may not fire endReached for that tail again. Expose an
        // idempotent retry so the footer can continue from the preserved
        // cursor instead of stranding pagination.
        setCanRetryLoadMore(true);
        return;
      }
      // A failed search replacement leaves the old query's cursor in place.
      // Never append against it with the newly debounced query; the explicit
      // retry below will issue a safe first-page reset instead.
      if (debouncedQueryRef.current !== listQueryRef.current) {
        setCanRetryLoadMore(true);
        return;
      }
      if (
        !hasMoreRef.current
        || loadingMoreRef.current
        || !nextCursorRef.current
      ) {
        setCanRetryLoadMore(false);
        return;
      }

      const cursor = nextCursorRef.current;
      const query = listQueryRef.current;
      // Set the ref synchronously so two endReached callbacks in one render
      // cannot launch duplicate page requests before React commits the state.
      loadingMoreRef.current = true;
      setLoadingMore(true);
      setCanRetryLoadMore(false);
      setError(null);
      try {
        const applied = await fetchPage(cursor, query, true);
        if (applied) return;
        // A replacement either won the race before this append started or
        // superseded it while it was in flight. Retry against its fresh page.
      } catch (err: any) {
        setError(err?.message ?? i18n.t("sidebar.errors.loadMoreSessions"));
        setCanRetryLoadMore(true);
        return;
      } finally {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [fetchPage, waitForReplacement]);

  const retryLoadMore = useCallback(async () => {
    // If a search replacement failed, its tail cursor belongs to the previous
    // query. Retry the first page for the current query rather than appending
    // mismatched results. For the usual background-refresh failure, this is
    // an ordinary idempotent continuation from the preserved tail cursor.
    if (debouncedQueryRef.current !== listQueryRef.current) {
      await refresh();
      return;
    }
    await loadMore();
  }, [loadMore, refresh]);

  // ── create() ──────────────────────────────────────────────────────────────
  // Local prepend instead of a full refresh() round-trip after POST.  The
  // POST 201 response already carries the full Session object (id, created_at,
  // last_active_at, agent_id, title ...), so there is no missing data to fetch
  // back.  Prepending saves an entire GET /api/sessions on every new-session
  // create, which is one of the requests observed in the multi-fire bug.
  //
  // IMPORTANT: create() must NOT set loading=true.  The ``loading`` flag
  // controls whether the session list is visible (the sidebar gates
  // ``{!loading && flatSessions.length > 0 && <Virtuoso/>}``).  Setting it
  // during create would unmount the list, causing a white flash and hiding
  // all existing sessions until the POST completes.  The ``loading`` flag is
  // reserved for initial page loads and full refreshes where replacing the
  // entire list is the intended behaviour.
  const create = useCallback(
    async (
      title?: string,
      agentId?: string | null,
      metadata?: SessionCreateRequest["metadata"],
      projectId?: string | null,
    ): Promise<Session | null> => {
      setError(null);
      try {
        const body: SessionCreateRequest = {
          title: title ?? DEFAULT_SESSION_TITLE,
          platform: "web",
          agent_id: agentId,
          ...(metadata ? { metadata } : {}),
          ...(projectId ? { project_id: projectId } : {}),
        };
        let res = await fetchWithAuth(
          API_BASE,
          { method: "POST", headers: jsonHeaders, body: JSON.stringify(body) },
          getToken
        );
        // Older server deployments reject the new top-level project_id field
        // during request validation. Retry once without it so independently
        // deployed UIs can still open an unbound conversation. Do not retry a
        // successful response that simply omits project_id: that would create
        // a duplicate unbound session on deployments that ignore unknown keys.
        if (!res.ok && projectId && (res.status === 400 || res.status === 422)) {
          const { project_id: _projectId, ...legacyBody } = body;
          res = await fetchWithAuth(
            API_BASE,
            { method: "POST", headers: jsonHeaders, body: JSON.stringify(legacyBody) },
            getToken
          );
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const session: Session = await res.json();
        // The upload hook sends files before /chat/web. Finish a remote
        // session's signed dial before reporting this new conversation as
        // selectable, so its first upload and run share the target UUID.
        if (agentId && isRemoteAgentRef(agentId)) {
          await dialRemoteSession(session.id, agentId, getToken);
        }
        // Register as pending so a concurrent/interleaved refreshSessions()
        // doesn't blow it away when replacing the list.
        pendingCreatedRef.current = [session, ...pendingCreatedRef.current];
        // Add to knownSessionIds immediately so the stale-session guard
        // doesn't treat a just-created session as stale before the next
        // refresh includes it.
        setKnownSessionIds((prev) => new Set(prev).add(session.id));
        // Prepend locally.  We deliberately avoid `commitSessions((prev) => refresh().then(...))`
        // here — that would still re-fetch the list.  Functional update keeps
        // ordering stable even if a concurrent caller updates the list in
        // parallel (e.g. rename racing with create).
        commitSessions((prev) => {
          // Avoid duplicate if the session is already in the list (e.g. a
          // refresh already included it).
          if (prev.some((s) => s.id === session.id)) return prev;
          return [session, ...prev];
        });
        void invalidateCache("sessionPages");
        return session;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("sidebar.errors.createSession"));
        return null;
      }
    },
    // refresh() is no longer a dependency because create() no longer calls it.
    [commitSessions, getToken]
  );

  // ── updateAgent() — optimistic local update ───────────────────────────────
  // With pagination, calling refresh() after every update would lose the
  // user's scroll position and loaded pages. Instead we optimistically patch
  // the local array and only fall back to refresh() on error.
  const updateAgent = useCallback(
    async (id: string, agentId: string | null): Promise<Session | null> => {
      try {
        // A remote ref uses the dedicated dial endpoint instead of PATCHing
        // the source session. The source row stays empty; the durable dial
        // routes every subsequent session-scoped operation to the target.
        if (agentId && isRemoteAgentRef(agentId)) {
          const dialed = await dialRemoteSession(id, agentId, getToken);
          if (dialed) {
            const current = sessions.find((session) => session.id === id);
            const session = current
              ? { ...current, agent_id: agentId }
              : ({ id, agent_id: agentId } as Session);
            commitSessions((prev) =>
              prev.map((item) => (item.id === id ? session : item))
            );
            void invalidateCache("sessionPages");
            return session;
          }
        }

        // Feature flag off (or a local agent) preserves the pre-transfer
        // source-session update behavior.
        commitSessions((prev) =>
          prev.map((s) => (s.id === id ? { ...s, agent_id: agentId } : s))
        );
        const body: SessionUpdateRequest = { agent_id: agentId };
        const res = await fetchWithAuth(
          `${API_BASE}/${id}`,
          { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(body) },
          getToken
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const session: Session = await res.json();
        // Update with server-confirmed value (may differ if server normalizes)
        commitSessions((prev) =>
          prev.map((s) => (s.id === id ? { ...s, agent_id: session.agent_id } : s))
        );
        void invalidateCache("sessionPages");
        return session;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("sidebar.errors.updateAgent"));
        // Revert via full refresh — server truth is authoritative on error
        await refresh();
        return null;
      }
    },
    [commitSessions, getToken, refresh, sessions]
  );

  // ── rename() — optimistic local update ────────────────────────────────────
  const rename = useCallback(
    async (id: string, title: string): Promise<Session | null> => {
      // Optimistic: update the title in the local array immediately
      commitSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, title } : s))
      );
      try {
        const body: SessionUpdateRequest = { title };
        const res = await fetchWithAuth(
          `${API_BASE}/${id}`,
          { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(body) },
          getToken
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const session: Session = await res.json();
        // Confirm with server response (title may be normalized)
        commitSessions((prev) =>
          prev.map((s) => (s.id === id ? { ...s, title: session.title } : s))
        );
        void invalidateCache("sessionPages");
        void invalidateCache("contextDetails", "session", id);
        return session;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("sidebar.errors.renameSession"));
        // Revert via full refresh
        await refresh();
        return null;
      }
    },
    [commitSessions, getToken, refresh]
  );

  // ── archive() — optimistic local removal ──────────────────────────────────
  const archive = useCallback(
    async (id: string): Promise<boolean> => {
      // Optimistic: remove from the local array immediately
      commitSessions((prev) => prev.filter((s) => s.id !== id));
      // Also remove from knownSessionIds so the stale-session guard treats
      // an archived session as genuinely gone.
      setKnownSessionIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      try {
        const res = await fetchWithAuth(
          `${API_BASE}/${id}`,
          { method: "DELETE", headers: {} },
          getToken
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        void invalidateCache("sessionPages");
        void invalidateCache("contextDetails", "session", id);
        void invalidateCache("messageTails", "tail", id);
        return true;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("sidebar.errors.archiveSession"));
        // Revert via full refresh
        await refresh();
        return false;
      }
    },
    [commitSessions, getToken, refresh]
  );

  const getTenantDefault = useCallback(async (): Promise<TenantDefault | null> => {
    try {
      const res = await fetchWithAuth(TENANT_DEFAULT_URL, { headers: {} }, getToken);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data: TenantDefault = await res.json();
      return data;
    } catch (err: any) {
      console.warn("[useSessions] getTenantDefault failed:", err?.message);
      return null;
    }
  }, [getToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    sessions,
    loaded,
    cacheState,
    loading,
    loadingMore,
    hasMore,
    canRetryLoadMore,
    error,
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    knownSessionIds,
    registerKnownSessionIds,
    searching,
    create,
    rename,
    updateAgent,
    archive,
    removeParticipation,
    addParticipation,
    refresh,
    loadMore,
    retryLoadMore,
    getTenantDefault,
  };
}
